import styles from './DoctorAddMedicineToOrderForm.module.css';

import classNames from 'classnames';
// import { MedicineAutocomplete } from '../molecules';
import { format, parse } from 'date-fns';

import ForwardIcon from '../../assets/icons/forward.svg?react';
import { useQuery } from '@tanstack/react-query';
import { FC, useCallback, useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import InputMask from 'react-input-mask';

import { CommonApi } from '../../api';
import type { Medicine, ReferenceItem } from '../../model';
import { formatMedicinePlural } from '../../utils';
import { Select } from '../atoms';
import { SelectMedicineModal } from './MedicineListModal';

interface Props {
  onAdd(data: OrderMedicine): void;
}

interface TimeWithCount {
  timeId: number;
  value: string;
  count: number;
  eatingId: number;
}

export interface OrderMedicine {
  totalCount: number;
  count: number;
  frequency: ReferenceItem;
  medicine: Medicine;
  time: TimeWithCount[];
  duration: number;
  start: string;
}

interface FormFields {
  totalCount: number;
  count: number;
  frequencyId: number;
  medicine: Medicine;
  time: TimeWithCount[];
  duration: number;
  start: string;
}

export const DoctorAddMedicineToOrderForm: FC<Props> = ({ onAdd }) => {
  const [totalCount, setTotalCount] = useState<number>(0);

  const { data: timeList = [] } = useQuery({
    queryKey: [CommonApi.KEY, 'getReference', 202],
    queryFn: () => CommonApi.getReference(202),
  });

  const { data: frequencyList = [] } = useQuery({
    queryKey: [CommonApi.KEY, 'getReference', 200],
    queryFn: () => CommonApi.getReference(200),
  });

  const { data: countList = [] } = useQuery({
    queryKey: [CommonApi.KEY, 'getReference', 201],
    queryFn: () => CommonApi.getReference(201),
  });

  const { data: eatingList = [] } = useQuery({
    queryKey: [CommonApi.KEY, 'getReference', 205],
    queryFn: () => CommonApi.getReference(205),
  });

  const {
    control,
    handleSubmit,
    register,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<FormFields>({
    defaultValues: {
      time: [],
      count: 0,
      duration: 1,
    },
  });

  const selectedTimes = watch('time') || [];

  const [isModalOpen, setIsModalOpen] = useState(false);
  // const [selectedMedicine, setSelectedMedicine] = useState<Medicine | null>(null);

  const updateTotalCount = (times: TimeWithCount[]) => {
    const newTotalCount = times.reduce((acc, time) => acc + time.count, 0);
    setTotalCount(newTotalCount);
  };

  useEffect(() => {
    updateTotalCount(selectedTimes);
  }, [selectedTimes]);

  const handleTimeChange = (timeId: number, isChecked: boolean) => {
    const selectedTime = timeList.find((t) => t.id === timeId);
    if (!selectedTime) return;

    const updatedTimes = isChecked
      ? [
          ...selectedTimes,
          { timeId, value: selectedTime.value, count: 0, eatingId: 0 },
        ]
      : selectedTimes.filter((time) => time.timeId !== timeId);

    setValue('time', updatedTimes);
  };

  const handleTimeCountChange = (index: number, count: number) => {
    const updatedTimes = [...selectedTimes];
    updatedTimes[index].count = count;
    setValue('time', updatedTimes);
    updateTotalCount(updatedTimes);
  };

  const handleCountChange = (countId: string, index: number) => {
    const selectedCount = countList.find((item) => item.id === Number(countId));
    if (selectedCount) {
      const newCount = Number(selectedCount.value);
      const updatedTimes = selectedTimes.map((time, i) =>
        i === index ? { ...time, count: newCount } : time,
      );
      setValue('time', updatedTimes);
    }
  };

  const onSubmit: SubmitHandler<FormFields> = useCallback(
    (data) => {
      if (!data.medicine || !data.frequencyId || data.time.length === 0) {
        console.error('Не все обязательные поля заполнены');
        return;
      }
      const parsedDate = parse(data.start, 'dd-MM-yyyy', new Date());
      const formattedDate = format(parsedDate, 'yyyy-MM-dd');

      onAdd({
        totalCount: totalCount,
        count: data.time[0].count,
        frequency: frequencyList.find((item) => item.id === data.frequencyId)!,
        medicine: data.medicine,
        time: data.time.map((time) => ({
          ...timeList.find((t) => t.id === time.timeId)!,
          timeId: time.timeId,
          count: time.count,
          eatingId: time.eatingId,
        })),
        duration: data.duration,
        start: formattedDate,
      });
      reset({
        time: [],
        count: 0,
        duration: 1,
        totalCount: 0,
        frequencyId: undefined as unknown as number,
        medicine: undefined as unknown as Medicine,
        start: '',
      });
      setTotalCount(0);
      setIsModalOpen(false);
    },
    [frequencyList, onAdd, timeList, totalCount, reset],
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="w-full">
      <div className="p-[24px]">
        {/* <Controller
          control={control}
          name="medicine"
          rules={{
            required: true,
          }}
          render={({ field, fieldState }) => (
            <MedicineAutocomplete
              className="mb-[12px]"
              error={fieldState.error}
              onChange={field.onChange}
              value={field.value}
            />
          )}
        /> */}

        <Controller
          control={control}
          name="medicine"
          rules={{
            required: true,
          }}
          render={({ field }) => (
            <div className="mb-[12px] flex items-center gap-[8px]">
              <input
                type="text"
                readOnly
                className={classNames(
                  'input w-full cursor-pointer',
                  !field.value
                    ? 'text-brand-1/40 border-brand-2'
                    : 'text-black',
                )}
                value={field.value ? field.value.name : 'Лекарство'}
                onClick={() => setIsModalOpen(true)}
              />
              <SelectMedicineModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onMedicineSelect={(medicine) => {
                  field.onChange(medicine);
                  setIsModalOpen(false);
                }}
              />
            </div>
          )}
        />

        <Controller
          control={control}
          name="frequencyId"
          rules={{
            required: true,
          }}
          render={({ field, fieldState }) => {
            return (
              <Select
                className="mb-[24px]"
                error={fieldState.error}
                onChange={field.onChange}
                options={frequencyList.map((frequency) => ({
                  label: frequency.value,
                  value: frequency.id,
                }))}
                placeholder="Частота приема"
                value={field.value}
              />
            );
          }}
        />

        <div className="mb-[24px]">
          <span className="mb-[12px] block text-[14px]">Когда принимать</span>
          <div className="flex gap-[12px]">
            {timeList.map((time) => (
              <label className={styles.radio} key={time.id}>
                <input
                  className="hidden"
                  type="checkbox"
                  checked={selectedTimes.some((t) => t.timeId === time.id)}
                  onChange={(e) => handleTimeChange(time.id, e.target.checked)}
                />
                <span className="whitespace-nowrap leading-normal">
                  {time.value}
                </span>
              </label>
            ))}
          </div>

          {selectedTimes.map((time, index) => (
            <div key={time.timeId}>
              <div className="mt-[12px]">
                <span className="mb-[12px] block text-[14px]">
                  Сколько таблеток принимать за один прием ({time.value})
                </span>
                <input
                  {...register(`time.${index}.count`, {
                    required: true,
                    valueAsNumber: true,
                    min: 1,
                  })}
                  className={classNames(
                    'mb-[12px] input w-[130px]',
                    errors.time?.[index]?.count && 'input--invalid',
                  )}
                  step={1}
                  type="number"
                  onChange={(e) =>
                    handleTimeCountChange(index, Number(e.target.value))
                  }
                />
              </div>

              <div className="flex flex-wrap gap-[6px]">
                {countList.map((count) => (
                  <label className={styles.radio} key={count.id}>
                    <input
                      className="hidden"
                      type="radio"
                      name={`count-${index}`}
                      value={count.id}
                      onChange={(e) => handleCountChange(e.target.value, index)}
                    />
                    <span className="whitespace-nowrap leading-normal">
                      {count.value}&nbsp;
                      {formatMedicinePlural(Number(count.value))}
                    </span>
                  </label>
                ))}
              </div>

              <div className="mt-[12px]">
                <span className="mb-[12px] block text-[14px]">
                  Как принимать
                </span>
                <div className="flex gap-[12px]">
                  {eatingList.map((eating) => (
                    <label className={styles.radio} key={eating.id}>
                      <input
                        className="hidden"
                        type="radio"
                        name={`eating-${index}`}
                        value={eating.id}
                        onChange={() => {
                          const updatedTimes = [...selectedTimes];
                          updatedTimes[index] = {
                            ...updatedTimes[index],
                            eatingId: eating.id,
                          };
                          setValue('time', updatedTimes);
                        }}
                      />
                      <span className="whitespace-nowrap leading-normal">
                        {eating.value}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-[24px]">
          <span className="mb-[12px] block text-[14px]">
            Продолжительность приема (в днях)
          </span>
          <input
            type="number"
            {...register('duration', {
              required: true,
              valueAsNumber: true,
              min: 1,
            })}
            className={classNames(
              'mb-[12px] input w-[130px]',
              errors.duration && 'input--invalid',
            )}
            min={1}
          />
        </div>

        <Controller
          control={control}
          name="start"
          rules={{
            required: true,
          }}
          render={({ field, fieldState }) => {
            return (
              <InputMask
                {...field}
                alwaysShowMask
                className={classNames(
                  'input',
                  fieldState.invalid && 'input--invalid',
                )}
                mask="99-99-9999"
                maskPlaceholder={null}
                placeholder="Дата начала приема(ДД-ММ-ГГГГ)"
                type="tel"
                value={field.value || ''}
              />
            );
          }}
        />
      </div>
      <footer className="p-[24px]">
        <button
          className="button button--primary flex w-full items-center justify-between px-[20px] text-left"
          type="submit"
        >
          Добавить лекарство
          <ForwardIcon />
        </button>
      </footer>
    </form>
  );
};
